// src/shared/api-client/endpoints/federation.js
// Federation API endpoints (web)

import { baseApi } from "../baseApi";

const federationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // -------------------------------------------------------------------------
    // DELEGATIONS
    // -------------------------------------------------------------------------

    getDelegations: builder.query({
      query: () => "/api/federation/delegations",
      providesTags: [{ type: "FederationDelegation", id: "LIST" }],
    }),

    // -------------------------------------------------------------------------
    // FEDERATION POSTS
    // -------------------------------------------------------------------------

    getFederationPosts: builder.query({
      query: (params = {}) => ({ url: "/api/federation/posts", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...(result.data.posts || []).map(({ id }) => ({
                type: "FederationPost",
                id,
              })),
              { type: "FederationPost", id: "LIST" },
            ]
          : [{ type: "FederationPost", id: "LIST" }],
    }),

    createFederationPost: builder.mutation({
      query: (data) => ({ url: "/api/federation/posts", method: "POST", body: data }),
      invalidatesTags: [{ type: "FederationPost", id: "LIST" }],
    }),

    repostFederationContent: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/posts/${id}/repost`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationPost", id: "LIST" }],
    }),

    // -------------------------------------------------------------------------
    // FEDERATION CONTRACTS
    // -------------------------------------------------------------------------

    getFederationContracts: builder.query({
      query: (params = {}) => ({ url: "/api/federation/contracts", params }),
      providesTags: [{ type: "FederationContract", id: "LIST" }],
    }),

    getFederationContractById: builder.query({
      query: (localId) => `/api/federation/contracts/${localId}`,
      providesTags: (_result, _error, localId) => [
        { type: "FederationContract", id: localId },
      ],
    }),

    // -------------------------------------------------------------------------
    // FEDERATION LEADERSHIP
    // -------------------------------------------------------------------------

    getFederationLeadership: builder.query({
      query: () => "/api/federation/leadership",
      providesTags: [{ type: "FederationLeadership", id: "LIST" }],
    }),

    getFederationDelegates: builder.query({
      query: () => "/api/federation/leadership/delegates",
      providesTags: [{ type: "FederationDelegates", id: "LIST" }],
    }),

    updateLeadershipSharing: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/leadership/${id}/share`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FederationLeadership", id },
        { type: "FederationLeadership", id: "LIST" },
      ],
    }),

    // -------------------------------------------------------------------------
    // FEDERATION OFFICIALS
    // -------------------------------------------------------------------------

    getFederationOfficials: builder.query({
      query: (params = {}) => ({ url: "/api/federation/officials", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...(result.data.officials || []).map(({ id }) => ({
                type: "FederationOfficial",
                id,
              })),
              { type: "FederationOfficial", id: "LIST" },
            ]
          : [{ type: "FederationOfficial", id: "LIST" }],
    }),

    getFederationOfficialById: builder.query({
      query: (id) => `/api/federation/officials/${id}`,
      providesTags: (_result, _error, id) => [{ type: "FederationOfficial", id }],
    }),

    createFederationOfficial: builder.mutation({
      query: (data) => ({ url: "/api/federation/officials", method: "POST", body: data }),
      invalidatesTags: [{ type: "FederationOfficial", id: "LIST" }],
    }),

    updateFederationOfficial: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/officials/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FederationOfficial", id },
        { type: "FederationOfficial", id: "LIST" },
      ],
    }),

    addOfficialNote: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/officials/${id}/notes`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "FederationOfficial", id }],
    }),

    trackOfficial: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/officials/${id}/tracking`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PoliticalOfficial", id },
        { type: "PoliticalOfficial", id: "LIST" },
        { type: "FederationOfficial", id },
      ],
    }),

    getFederationOfficialInteractions: builder.query({
      query: (id) => `/api/federation/officials/${id}/interactions`,
      providesTags: (_result, _error, id) => [
        { type: "FederationOfficial", id: `INTERACTIONS_${id}` },
      ],
    }),

    createFederationOfficialInteraction: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/officials/${id}/interactions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FederationOfficial", id: `INTERACTIONS_${id}` },
        { type: "FederationOfficial", id },
      ],
    }),

    // -------------------------------------------------------------------------
    // FEDERATION JURISDICTIONS
    // -------------------------------------------------------------------------

    getFederationJurisdictions: builder.query({
      query: () => "/api/federation/jurisdictions",
      providesTags: [{ type: "FederationJurisdiction", id: "LIST" }],
    }),

    createFederationJurisdiction: builder.mutation({
      query: (data) => ({
        url: "/api/federation/jurisdictions",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationJurisdiction", id: "LIST" }],
    }),

    // -------------------------------------------------------------------------
    // ADMIN ENDPOINTS
    // -------------------------------------------------------------------------

    adminSearchTenants: builder.query({
      query: (q = "") => ({ url: "/api/federation/admin/tenants/search", params: { q } }),
    }),

    adminGetMembers: builder.query({
      query: (params = {}) => ({ url: "/api/federation/admin/memberships", params }),
      providesTags: [{ type: "FederationMember", id: "LIST" }],
    }),

    adminGetPolicy: builder.query({
      query: () => "/api/federation/admin/policy",
      providesTags: [{ type: "FederationPolicy", id: "CURRENT" }],
    }),

    adminGetParentGrants: builder.query({
      query: () => "/api/federation/admin/parent-grants",
      providesTags: [{ type: "FederationPolicy", id: "PARENT_GRANTS" }],
    }),

    adminGetAuditLog: builder.query({
      query: (params = {}) => ({ url: "/api/federation/admin/audit-log", params }),
      providesTags: [{ type: "FederationAuditLog", id: "LIST" }],
    }),

    adminCreateRoot: builder.mutation({
      query: (data) => ({
        url: "/api/federation/admin/roots",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationDelegation", id: "LIST" }],
    }),

    adminAddMember: builder.mutation({
      query: (data) => ({
        url: "/api/federation/admin/memberships",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationMember", id: "LIST" }],
    }),

    adminRemoveMember: builder.mutation({
      query: (id) => ({
        url: `/api/federation/admin/memberships/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "FederationMember", id: "LIST" }],
    }),

    adminListDelegations: builder.query({
      query: () => "/api/federation/admin/delegations",
      providesTags: [{ type: "FederationDelegation", id: "LIST" }],
    }),

    adminGrantDelegation: builder.mutation({
      query: (data) => ({
        url: "/api/federation/admin/delegations",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationDelegation", id: "LIST" }],
    }),

    adminRevokeDelegation: builder.mutation({
      query: (id) => ({
        url: `/api/federation/admin/delegations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "FederationDelegation", id: "LIST" }],
    }),

    adminSearchMemberTenantUsers: builder.query({
      query: ({ tenantId, search = "", limit = 20 }) => ({
        url: `/api/federation/admin/member-tenants/${tenantId}/users`,
        params: { search, limit },
      }),
    }),

    adminGetLocalMembers: builder.query({
      query: ({ tenantId, search, page = 1, limit = 20 }) => ({
        url: `/api/federation/admin/member-tenants/${tenantId}/members`,
        params: { search, page, limit },
      }),
      providesTags: (_result, _error, { tenantId }) => [
        { type: "FederationLocalMembers", id: tenantId },
      ],
    }),

    adminGetDelegationCeiling: builder.query({
      query: () => "/api/federation/admin/delegation-ceiling",
      providesTags: [{ type: "FederationPolicy", id: "CEILING" }],
    }),

    adminUpdatePolicy: builder.mutation({
      query: (data) => ({
        url: "/api/federation/admin/policy",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: "FederationPolicy", id: "CURRENT" }],
    }),
  }),
});

// Delegations
export const useGetDelegationsQuery = federationApi.useGetDelegationsQuery;

// Federation Posts
export const useGetFederationPostsQuery = federationApi.useGetFederationPostsQuery;
export const useCreateFederationPostMutation = federationApi.useCreateFederationPostMutation;
export const useRepostFederationContentMutation = federationApi.useRepostFederationContentMutation;

// Federation Contracts
export const useGetFederationContractsQuery = federationApi.useGetFederationContractsQuery;
export const useGetFederationContractByIdQuery = federationApi.useGetFederationContractByIdQuery;

// Federation Leadership
export const useGetFederationLeadershipQuery = federationApi.useGetFederationLeadershipQuery;
export const useGetFederationDelegatesQuery = federationApi.useGetFederationDelegatesQuery;
export const useUpdateLeadershipSharingMutation = federationApi.useUpdateLeadershipSharingMutation;

// Federation Officials
export const useGetFederationOfficialsQuery = federationApi.useGetFederationOfficialsQuery;
export const useGetFederationOfficialByIdQuery = federationApi.useGetFederationOfficialByIdQuery;
export const useCreateFederationOfficialMutation = federationApi.useCreateFederationOfficialMutation;
export const useUpdateFederationOfficialMutation = federationApi.useUpdateFederationOfficialMutation;
export const useAddOfficialNoteMutation = federationApi.useAddOfficialNoteMutation;
export const useTrackOfficialMutation = federationApi.useTrackOfficialMutation;
export const useGetFederationOfficialInteractionsQuery = federationApi.useGetFederationOfficialInteractionsQuery;
export const useCreateFederationOfficialInteractionMutation = federationApi.useCreateFederationOfficialInteractionMutation;

// Federation Jurisdictions
export const useGetFederationJurisdictionsQuery = federationApi.useGetFederationJurisdictionsQuery;
export const useCreateFederationJurisdictionMutation = federationApi.useCreateFederationJurisdictionMutation;

// Admin
export const useAdminSearchTenantsQuery = federationApi.useAdminSearchTenantsQuery;
export const useAdminGetMembersQuery = federationApi.useAdminGetMembersQuery;
export const useAdminGetPolicyQuery = federationApi.useAdminGetPolicyQuery;
export const useAdminGetParentGrantsQuery = federationApi.useAdminGetParentGrantsQuery;
export const useAdminGetAuditLogQuery = federationApi.useAdminGetAuditLogQuery;
export const useAdminCreateRootMutation = federationApi.useAdminCreateRootMutation;
export const useAdminAddMemberMutation = federationApi.useAdminAddMemberMutation;
export const useAdminRemoveMemberMutation = federationApi.useAdminRemoveMemberMutation;
export const useAdminListDelegationsQuery = federationApi.useAdminListDelegationsQuery;
export const useAdminGrantDelegationMutation = federationApi.useAdminGrantDelegationMutation;
export const useAdminRevokeDelegationMutation = federationApi.useAdminRevokeDelegationMutation;
export const useLazyAdminSearchMemberTenantUsersQuery =
  federationApi.useLazyAdminSearchMemberTenantUsersQuery;
export const useAdminGetLocalMembersQuery = federationApi.useAdminGetLocalMembersQuery;
export const useAdminGetDelegationCeilingQuery =
  federationApi.useAdminGetDelegationCeilingQuery;
export const useAdminUpdatePolicyMutation = federationApi.useAdminUpdatePolicyMutation;
